import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Image,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useMutation } from 'react-query';
import { mutateFunction } from '../../../../libs/apis';
import { CustomToast } from '../../../toast/CustomToast';

const EditEventModal = ({ isOpen, onClose, event, onSuccess }) => {
  const [data, setData] = useState({});
  const toast = useToast();

  const { addToast } = CustomToast();

  const { mutate, isLoading } = useMutation(mutateFunction);

  useEffect(() => {
    if (event) {
      setData({
        name: event.name || '',
        start_date: event.start_date
          ? new Date(event.start_date).toISOString().slice(0, 16)
          : '',
        end_date: event.end_date
          ? new Date(event.end_date).toISOString().slice(0, 16)
          : '',
        location: event.location || '',
        cover_image: event.cover_image || '',
        event_link: event.event_link || '',
        description: event.description || '',
        status: event.status || 'public',
      });
    }
  }, [event]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    // Basic file handling, you might need more advanced logic for uploads
    const file = e.target.files[0];
    if (file) {
      setData((prev) => ({
        ...prev,
        cover_image: { file, url: URL.createObjectURL(file) },
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Omitting file for now from submission
    const { cover_image, ...submissionData } = data;

    let formData = new FormData();
    formData.append('name', submissionData.name);
    formData.append('start_date', submissionData.start_date);
    formData.append('end_date', submissionData.end_date);
    formData.append('location', submissionData.location);
    formData.append('description', submissionData.description || '');
    formData.append('event_link', submissionData.event_link || '');
    formData.append('status', submissionData.status);

    // Handle cover image if it exists
    if (cover_image) {
      if (typeof cover_image === 'object') {
        formData.append('cover_image', cover_image.file);
      } else {
        formData.append('cover_image', cover_image);
      }
    }

    mutate(
      {
        key: `admins/events/${event.id}`,
        method: 'put',
        data: formData,
      },
      {
        onSuccess: (res) => {
          addToast(res);
          onSuccess();
        },
        onError: (error) => {
          addToast(
            error?.response?.data?.message || 'Error updating event.',
            'error',
          );
        },
      },
    );
  };

  if (!event) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent
        w={{ base: '90%', md: '500px' }}
        maxW={{ base: '90%', md: '500px' }}
        maxH="90vh"
        overflow="hidden"
        as="form"
        onSubmit={handleSubmit}
      >
        <ModalHeader>Edit Event</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} overflowY="auto" className="hide-scroll">
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Event Name</FormLabel>
              <Input name="name" value={data.name} onChange={handleChange} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Start Date/Time</FormLabel>
              <Input
                type="datetime-local"
                name="start_date"
                value={data.start_date}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl>
              <FormLabel>End Date/Time</FormLabel>
              <Input
                type="datetime-local"
                name="end_date"
                value={data.end_date}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Location</FormLabel>
              <Input
                name="location"
                value={data.location}
                onChange={handleChange}
                placeholder="e.g. Online, New York"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Cover Image</FormLabel>
              {data.cover_image && (
                <Image
                  src={
                    typeof data.cover_image === 'object'
                      ? data.cover_image.url
                      : data.cover_image
                  }
                  alt="Current cover"
                  boxSize="150px"
                  objectFit="cover"
                  mb={2}
                />
              )}
              <Input type="file" onChange={handleFileChange} />
            </FormControl>
            <FormControl>
              <FormLabel>Event Link</FormLabel>
              <Input
                name="event_link"
                value={data.event_link}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                name="description"
                value={data.description}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Visibility Status</FormLabel>
              <Select name="status" value={data.status} onChange={handleChange}>
                <option value="public">Public</option>
                <option value="hidden">Hidden</option>
                <option value="flagged">Flagged</option>
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" colorScheme="blue" isLoading={isLoading}>
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditEventModal;
